import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Switch, FormControlLabel,
  Snackbar, Alert, CircularProgress, Stack, Divider, TextField,
} from '@mui/material';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import PageHeader from '../components/PageHeader';
import RankStar from '../components/RankStar';
import { skillSettingsApi, getApiErrorMessage } from '../services/api';
import { SkillTier, StoreRankConfig } from '../types';
import { useAuth } from '../auth/AuthContext';
import { useManagementAuth } from '../auth/ManagementAuthContext';

/**
 * ランク設定画面（旧: ジャンル別ランク設定）。
 * 2026-07 改訂: ジャンル管理・集約モードは廃止し、ランクは「tier別スキルカタログの達成率」に一本化。
 * 2026-07-17 改訂: スキルが店舗ごとにマスター＋追加される運用に合わせ、各ランクの達成率
 * （昇格閾値）を店舗ごとに設定可能へ再導入。
 * 2026-07-24 改訂: 判定基準を「個数」から「達成率(%)」へ変更。あるランクのスキルを達成率分
 * 習得すると次のランクに進む（未設定は100%）ラダー型。ルーキーも自分のスキルカタログを持ち、
 * 達成率を設定できる（固定の「スキル0個」表示ではなくなった）。
 * 本画面で編集できるのは tier別達成率(%)と、店舗ランク設定（勤務実績ゲートの ON/OFF）。
 */
const RankSettings: React.FC = () => {
  const { storeId } = useAuth();
  const { managementToken } = useManagementAuth();
  const token = managementToken || localStorage.getItem('management_token') || '';

  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState<SkillTier[]>([]);
  const [rankConfig, setRankConfig] = useState<StoreRankConfig | null>(null);
  const [workPerf, setWorkPerf] = useState(false);
  // tier_id -> 達成率(%)
  const [thresholds, setThresholds] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const notify = (msg: string, sev: 'success' | 'error') => setSnack({ msg, sev });

  const loadAll = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    try {
      const [t, rc] = await Promise.all([
        skillSettingsApi.getSkillTiers(storeId, token),
        skillSettingsApi.getRankConfig(storeId, token),
      ]);
      const sortedTiers = [...t].sort((a, b) => a.sort_order - b.sort_order);
      setTiers(sortedTiers);
      setRankConfig(rc);
      setWorkPerf(rc.work_performance_enabled);
      // rank-config の thresholds（全 active tier の実効値）から編集 state を構築
      const map: Record<string, number> = {};
      (rc.thresholds ?? []).forEach((th) => { map[th.tier_id] = th.achievement_rate; });
      setThresholds(map);
    } catch (e) {
      notify(getApiErrorMessage(e, '設定の読み込みに失敗しました'), 'error');
    } finally {
      setLoading(false);
    }
  }, [storeId, token]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleThresholdChange = (tierId: string, raw: string) => {
    const n = raw === '' ? 0 : Number(raw);
    if (Number.isNaN(n)) return;
    setThresholds((prev) => ({ ...prev, [tierId]: Math.min(100, Math.max(0, n)) }));
  };

  /** 達成率の妥当性（0以上100以下の数値）を検証する。tierごとに分母(カタログ数)が異なるため上位ランクほど大きい必要はない。 */
  const validateThresholds = (): string | null => {
    for (const t of tiers) {
      const v = thresholds[t.tier_id] ?? 0;
      if (Number.isNaN(v) || v < 0 || v > 100) {
        return '達成率は0以上100以下の数値で入力してください';
      }
    }
    return null;
  };

  const save = async () => {
    const err = validateThresholds();
    if (err) {
      notify(err, 'error');
      return;
    }
    setBusy(true);
    try {
      // 全 tier（ルーキー含む）を送信＝店舗別達成率の全置換。
      const payloadThresholds = tiers
        .map((t) => ({ tier_id: t.tier_id, achievement_rate: thresholds[t.tier_id] ?? 0 }));
      await skillSettingsApi.putRankConfig(
        storeId,
        { work_performance_enabled: workPerf, thresholds: payloadThresholds },
        token,
      );
      notify('ランク設定を保存しました', 'success');
      loadAll();
    } catch (e) {
      notify(getApiErrorMessage(e, '設定の保存に失敗しました'), 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 2, maxWidth: 880, mx: 'auto' }}>
      <PageHeader title="ランク設定" icon={<MilitaryTechIcon />} />

      {/* ランク基準（tier別達成率・店舗ごとに編集可） */}
      <Paper sx={{ borderRadius: '12px', boxShadow: 'none', border: '1px solid', borderColor: 'divider', p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>ランク基準（達成率）</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          クルーのランクは、各ランクに設定されたスキルをどれだけ習得したかで決まります。
          あるランクのスキルを達成率分習得すると、次のランクに進みます（未設定は100%）。
        </Typography>
        <Stack spacing={1.5}>
          {tiers.map((t) => (
            <Box key={t.tier_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ minWidth: 120 }}>
                <RankStar tierCode={t.code} color={t.color} label={t.name} showLabel size={18} />
              </Box>
              <TextField
                type="number"
                size="small"
                label="達成率(%)"
                value={thresholds[t.tier_id] ?? 0}
                onChange={(e) => handleThresholdChange(t.tier_id, e.target.value)}
                inputProps={{ min: 0, max: 100, step: 0.1, 'aria-label': `${t.name}の達成率` }}
                sx={{ width: 150 }}
              />
            </Box>
          ))}
        </Stack>
      </Paper>

      {/* 店舗ランク設定 */}
      <Paper sx={{ borderRadius: '12px', boxShadow: 'none', border: '1px solid', borderColor: 'divider', p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>店舗ランク設定</Typography>
        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={<Switch checked={workPerf} onChange={(e) => setWorkPerf(e.target.checked)} />}
            label="勤務実績を昇格条件に加味する"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 6, wordBreak: 'break-all', whiteSpace: 'normal' }}>
            ONにすると、スキル数の条件に加えて勤務実績（総シフト数・信頼スコア）の条件も満たして初めてランクが上がります。
          </Typography>
        </Box>
        <Button variant="contained" onClick={save} disabled={busy || !rankConfig}>保存</Button>
      </Paper>

      <Snackbar
        open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? <Alert severity={snack.sev} onClose={() => setSnack(null)}>{snack.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default RankSettings;
